import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo-hooks'
import styled from 'react-emotion'
import Markdown from 'react-markdown'
import { ga, Svg } from '../../application'
import { tests, urls } from '../../resources'
import { colors, Divider, Message } from '../../styles'
import { BookingProvider } from '../bookingContext'
import * as Mutations from '../mutations'
import * as Queries from '../queries'
import BookingAncillaries from '../recommendedAncillaries'
import getText from '../strings'
import BookingDetailButton from './BookingDetailButton'
import BookingFooter from './BookingFooter'
import { ButtonRow, ContentContainer, ContentContainerCenter, ContentContainerTop, ProductContainer } from './components'
import Countdown from './Countdown'
import ExcursionWidget from './ExcursionWidget'
import HotelInfo from './HotelInfo'
import Image from './Image'
import InviteUsers from './InviteUsers'
import Product from './Product'
import SelfService from './SelfService'

const ImageContainer = styled('div')`
  display: flex;
  flex-direction: column;
  min-width: 320px;
  min-height: 200px;
  position: relative;

  > div:first-child {
    border-bottom: 2px solid ${colors.tui_white};
  }
  > div:last-child {
    border-top: 2px solid ${colors.tui_white};
  }

  @media (max-width: 992px) {
    flex-direction: row;
    > div:first-child {
      border-bottom: unset;
      border-right: 2px solid ${colors.tui_white};
    }
    > div:last-child {
      border-top: unset;
      border-left: 2px solid ${colors.tui_white};
    }
  }
`

const ImageCombiner = styled('div')`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 4px solid ${colors.tui_white};
  border-radius: 50%;
  height: 50px;
  width: 50px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  z-index: 10;
  background-color: ${colors.tui_yellow};
  color: ${colors.tui_white};

  .pictogram {
    width: 40px;
    height: 40px;
  }
`

const CoopBookingHeader = styled('div')`
  display: flex;
  position: relative;
  background-color: #fa1119;
  font-size: 1em;
  color: #fff;
  z-index: 1000;
  padding: 10px;
  top: 22px;
  border-radius: 4px 4px 0 0;
  p {
    margin: unset;
  }
  a {
    color: ${colors.tui_white};
    font-weight: bold;
  }
`

const AtiBookingHeader = styled('div')`
  display: flex;
  position: relative;
  background-color: ${colors.tui_beige_50};
  font-size: 1em;
  z-index: 1000;
  padding: 10px;
  top: 22px;
  border-radius: 4px 4px 0 0;
  p {
    margin: unset;
  }
  span {
    margin-left: 6px;
  }
`

const EmptyContainer = styled('div')`
  flex-grow: 1;
`

const Cancelled = styled('div')`
  font-weight: bold;
  color: ${colors.tui_red};
  font-size: 1.2em;
`

const BookingNumber = styled('div')`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  text-align: right;
  padding-right: 15px;
  @media (max-width: 992px) {
    margin-top: 10px;
    flex-direction: row;
    span {
      margin: 0 2px;
    }
  }
`

const BookingDetails = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 15px;
  @media (max-width: 992px) {
    flex-direction: column;
  }
`

const ButtonSection = styled('div')`
  display: flex;
  flex-direction: column;
`

const DueDate = styled('div')`
  font-size: 16px;
  margin-top: 12px;
`

const PayButton = styled('button')`
  flex-shrink: 0;
  width: auto;
  margin-bottom: 0px;
  margin-left: 20px;
  @media (max-width: 992px) {
    margin-top: 20px;
    margin-left: 0;
    width: 100%;
  }
`

export default ({ booking, isNextUpcomingTrip }) => {
  const [bookingData, setBookingData] = useState(booking)
  const [partners, setPartners] = useState([])
  const { data: queryResult, refetch } = useQuery(Queries.getBooking(), {
    variables: {
      bookingNumber: booking.bookingNumber,
      disableCache: false,
    },
  })
  const [isLoadingPaymentUrl, setIsLoadingPaymentUrl] = useState(false)
  const [paymentUrlError, setPaymentUrlError] = useState(null)
  const [atiFilledIn, setAtiFilledIn] = useState(null)
  const { loading: atiFilledInLoading, data: atiFilledInData } = useQuery(Queries.getAtiFilledIn(booking.bookingNumber))

  useEffect(() => {
    const data = Object.assign(booking, queryResult.booking)
    setBookingData({ ...data })
    setPartners(queryResult.partners || [])
  }, [queryResult])

  useEffect(() => {
    if (!atiFilledInLoading && atiFilledInData) {
      setAtiFilledIn(atiFilledInData.atiFilledIn)
    }
  }, [atiFilledInData])
  const updateInvite = useMutation(Mutations.updateBookingInvites, {
    update: () => refetch(),
  })

  const revokeInvite = useMutation(Mutations.revokeBookingInvite, {
    update: () => refetch(),
  })

  const bookingCancelled = () => {
    refetch({ disableCache: true })
  }

  const removeBooking = useMutation(Mutations.removeBooking, {
    update: () => {
      window.location.reload()
    },
  })

  const getPaymentUrl = useMutation(Mutations.getPaymentUrl, {
    update: (_, mutationResult) => {
      if (mutationResult.data.paymentUrl && mutationResult.data.paymentUrl.url) {
        window.location.href = mutationResult.data.paymentUrl.url
      } else {
        setIsLoadingPaymentUrl(false)
      }
    },
  })

  const travelWithIn14days = (product) => {
    const startDate = product.startDate
    const daysUntilHoliday = moment(startDate).diff(moment(), 'days') + 1
    if (daysUntilHoliday < 15) {
      return true
    }
    return false
  }
  const { bookingNumber, startDate, endDate, products, leadPassenger, bookingSystem, redirectingToMyHoliday, hasFlights, agentCode } = bookingData
  const coopStatus = partners.find((p) => p.partnerId === 'COOPSE')
  const firstProduct = products[0]
  const sourceSystem = bookingData.packageType === 'HOTEL_ONLY' && firstProduct.productId && firstProduct.productId.startsWith('A') ? 'TRIPS' : 'LEGACY'
  const isCombi = products.length > 1
  const secondProduct = isCombi && products[1]
  const showSelfService = leadPassenger && tests.selfService
  const showBookingFooter = /removebooking|debug$/.test(window.location.href)
  let images = null
  let additionalInfo = null
  const isAtcomBooking = bookingSystem && bookingSystem === 'ATCOMRES'

  if (isCombi && firstProduct.image && secondProduct.image) {
    images = (
      <ImageContainer>
        <Image {...firstProduct} oneOfCombi={true} />
        <ImageCombiner>
          <Svg icon="plus" />
        </ImageCombiner>
        <Image {...secondProduct} oneOfCombi={true} />
      </ImageContainer>
    )
  } else {
    images = <Image {...firstProduct} />
  }

  /*if (firstProduct.countryId && firstProduct.countryId === 'G-000073') {
    additionalInfo = (
      <CovidBookingHeader>
        <span>
          {textBuilder(getText('covidFormInfoSpain', 'text'), <a onClick={() => handleCovidInfoLink(getText('covidFormInfoSpain', 'link'))}>&nbsp;{getText('covidFormInfoSpain', 'linkText')}</a>)}
        </span>
      </CovidBookingHeader>
    )
  }*/

  if (agentCode === '6107' && coopStatus && coopStatus.memberId === null) {
    additionalInfo = (
      <CoopBookingHeader>
        <span>
          <Markdown source={getText('selfservice', 'coop', 'reminder')} />
        </span>
      </CoopBookingHeader>
    )
  }

  if (!atiFilledInLoading && atiFilledIn && atiFilledIn.atiRequired && !atiFilledIn.atiFilledIn && travelWithIn14days(firstProduct)) {
    additionalInfo = (
      <AtiBookingHeader>
        <Svg icon="letter-info--circled" /> <span>{getText('selfservice', 'ati', 'header')}</span>
      </AtiBookingHeader>
    )
  }

  /*if (firstProduct.countryId && firstProduct.countryId === 'G-000058') {
    additionalInfo = (
      <CovidBookingHeader>
        <span>
          {textBuilder(getText('covidFormInfoCyprus', 'text'), <a onClick={() => handleCovidInfoLink(getText('covidFormInfoCyprus', 'link'))}>&nbsp;{getText('covidFormInfoCyprus', 'linkText')}</a>)}
        </span>
      </CovidBookingHeader>
    )
  }*/

  /*if (firstProduct.countryId && firstProduct.countryId === 'G-000000241') {
    additionalInfo = (
      <CovidBookingHeader>
        <span>
          {textBuilder(getText('covidFormInfoGreece', 'text'), <a onClick={() => handleCovidInfoLink(getText('covidFormInfoGreece', 'link'))}>&nbsp;{getText('covidFormInfoGreece', 'linkText')}</a>)}
        </span>
      </CovidBookingHeader>
    )
  }*/

  const handleCovidInfoLink = (link) => {
    window.open(link, '_blank')
  }

  const handleInviteUpdate = (updates) => {
    ga.buttonClick('added-invited-user')
    updateInvite({
      variables: {
        bookingNumber: bookingData.bookingNumber,
        leadPassengerName: bookingData.connectedUsers.filter((cu) => cu.leadPassenger)[0].firstName,
        signInUrl: urls.signInWithHost,
        signUpUrl: urls.signUpWithHost,
        invites: updates.map((u) => ({
          email: u.invitationEmail,
          passengerIndex: u.index,
        })),
      },
    })
  }

  const handleRevokeInvite = (index) => {
    ga.buttonClick('removed-invited-user')
    revokeInvite({
      variables: {
        bookingNumber: bookingData.bookingNumber,
        passengerIndex: index,
      },
    })
  }

  const handleRemoveBooking = () => {
    removeBooking({
      variables: {
        bookingNumber: bookingData.bookingNumber,
      },
    })
  }

  const handlePaymentClick = () => {
    setIsLoadingPaymentUrl(true)
    setPaymentUrlError(null)
    getPaymentUrl({
      variables: {
        bookingNumber: bookingData.bookingNumber,
        sourceSystem: sourceSystem,
      },
    }).catch((e) => {
      setIsLoadingPaymentUrl(false)
      setPaymentUrlError(getText('payLater', 'errorMessage'))
    })
  }

  return (
    <BookingProvider data={bookingData}>
      {tests.tuiblue && additionalInfo}
      <ProductContainer>
        {images}
        <ContentContainer>
          <ContentContainerTop>
            <Countdown endDate={endDate} startDate={startDate} />
            {bookingData.isCancelled ? <Cancelled>{getText('cancelledBooking')}</Cancelled> : <InviteUsers onInviteUpdate={handleInviteUpdate} onRevokeInvite={handleRevokeInvite} />}
          </ContentContainerTop>
          <ContentContainerCenter>
            {products.map((product, index) => (
              <Product key={index} product={product} startDate={startDate} />
            ))}
          </ContentContainerCenter>
          <Divider />
          {(!tests.GDN || bookingData.packageType !== 'HOTEL_ONLY') && (
            <ButtonRow>
              {leadPassenger && !bookingData.isCancelled ? (
                <BookingAncillaries hasFlights={hasFlights} bookingNumber={bookingNumber} startDate={startDate} isAtcomBooking={isAtcomBooking} />
              ) : (
                <EmptyContainer />
              )}
              <BookingDetailButton bookingNumber={bookingNumber} booking={bookingData} redirectingToMyHoliday={redirectingToMyHoliday} />
            </ButtonRow>
          )}
          {tests.GDN && bookingData.packageType === 'HOTEL_ONLY' && (
            <div>
              <HotelInfo booking={bookingData} />
              <Divider />
              {paymentUrlError && <Message error={true}>{paymentUrlError}</Message>}
              <BookingDetails>
                <BookingNumber>
                  <span>{getText('bookingNumber')}</span>
                  <span>
                    <strong>{bookingNumber}</strong>
                  </span>
                </BookingNumber>
                {bookingData.priceInfo && bookingData.priceInfo.remainingAmount > 0 && (
                  <ButtonSection>
                    <PayButton className="btn btn-function" disabled={isLoadingPaymentUrl} onClick={handlePaymentClick}>
                      {getText('payLater', 'title')}
                    </PayButton>
                    <DueDate>
                      {getText('payLater', 'dueDate')} {bookingData.priceInfo.totalPriceDueDate}
                    </DueDate>
                  </ButtonSection>
                )}
              </BookingDetails>
            </div>
          )}
        </ContentContainer>
      </ProductContainer>
      {showSelfService && !bookingData.isCancelled && atiFilledIn && <SelfService booking={booking} atiFilledIn={atiFilledIn.atiFilledIn} cancelledCallback={bookingCancelled} />}
      {isAtcomBooking && <ExcursionWidget booking={booking} isNextUpcomingTrip={isNextUpcomingTrip} />}
      {(showBookingFooter || bookingData.isCancelled) && <BookingFooter bookingNumber={bookingNumber} removeBooking={handleRemoveBooking} />}
    </BookingProvider>
  )
}
